import { ResolveFn, Router } from '@angular/router';
import { Firestore } from '@angular/fire/firestore';
import { inject } from '@angular/core';

import { BrandingService } from '@yoimo/joymo-ui';
import { getPageHeader, getChannelPageById } from '@yoimo/client-sdk/pages';
import { map, switchMap, EMPTY, catchError, of } from 'rxjs';

import { LoggingService, MetaService, ScopeService } from '@core/services';
import { mapPageToUIPage } from '@core/utilities/pages';
import { PageScope } from 'shared-lib/core';

/** Resolves the page scope from a page with fully dynamic content  */
export const pageResolver: ResolveFn<PageScope | undefined> = (route) => {
  const brandingService = inject(BrandingService);
  const fs = inject(Firestore);
  const metaService = inject(MetaService);
  const router = inject(Router);
  const scopeService = inject(ScopeService);

  const pageId = <string>route.paramMap.get('pageId');

  return scopeService.scope$.pipe(
    switchMap((scope) => {
      if (scope.scope === 'CHANNEL') {
        return getChannelPageById(fs, scope.channel.docId, pageId).pipe(
          map((page) => ({ channel: scope.channel, page }))
        );
      }
      throw new Error('NotImplemented');
    }),
    map(({ channel, page }) => {
      if (!page) throw new Error('PageNotFound:' + pageId);

      metaService.setTags({
        title: channel.name,
        description: page.name,
        image: '',
      });

      if (page.metaTags) {
        metaService.setRawTags(page.metaTags);
      }
      if (page.brandingOverride) {
        brandingService.setOverride(page.brandingOverride, true);
      }

      const header = page.header || getPageHeader(page, { channel });
      const uiPage = mapPageToUIPage(
        page,
        header,
        'DEFAULT',
        route.queryParams['tabId']
      );

      return {
        channel,
        activePage: uiPage,
        userHasAccess$: EMPTY,
      };
    }),
    catchError((err) => {
      LoggingService.logError(err);
      router.navigate(['/404'], { skipLocationChange: true });
      return of(undefined);
    })
  );
};
